/**
 * Generated by orval 🍺
 * Do not edit manually.
 * Tessera Backend API
 * Tessera backend application with Bearer token authentication and multi-tenant support
 */
import type {
  DataTag,
  DefinedInitialDataOptions,
  DefinedUseQueryResult,
  InvalidateOptions,
  QueryClient,
  QueryFunction,
  QueryKey,
  UndefinedInitialDataOptions,
  UseQueryOptions,
  UseQueryResult,
} from '@tanstack/react-query';
import { useQuery } from '@tanstack/react-query';

import type { ErrorType } from '../api-instance';
import { apiInstance } from '../api-instance';
import type {
  GetWorkflowNodeDocsV2PathParameters,
  GetWorkflowNodesV2Params,
  GetWorkflowNodeV2PathParameters,
  TLAvailableNodesResponse,
  TLHTTPValidationError,
  TLMcpToolsResponse,
  TLNodeDefinition,
  TLNodeDocumentationResponse,
} from './tessera-backend-api.schemas';

type SecondParameter<T extends (...args: never) => unknown> = Parameters<T>[1];

const withQueryKey = <T extends object, K>(query: T, queryKey: K): T & { queryKey: K } => {
  const result = { queryKey } as T & { queryKey: K };
  for (const key of Object.keys(query)) {
    // The explicit queryKey always wins, matching the previous
    // `{ ...query, queryKey }` spread where it was set last.
    if (key === 'queryKey') continue;
    Object.defineProperty(result, key, {
      enumerable: true,
      configurable: true,
      get: () => (query as Record<string, unknown>)[key],
    });
  }
  return result;
};

/**
 * @summary Get Workflow Nodes V2
 */
export const getWorkflowNodesV2 = (
  params?: GetWorkflowNodesV2Params,
  options?: SecondParameter<typeof apiInstance>,
  signal?: AbortSignal,
) => {
  return apiInstance<TLAvailableNodesResponse>(
    { url: `/api/v2/workflows/nodes`, method: 'GET', params, signal },
    options,
  );
};

export const getGetWorkflowNodesV2QueryKey = (params?: GetWorkflowNodesV2Params) => {
  return ['api', 'v2', 'workflows', 'nodes', ...(params ? [params] : [])] as const;
};

export const getGetWorkflowNodesV2QueryOptions = <
  TData = Awaited<ReturnType<typeof getWorkflowNodesV2>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  params?: GetWorkflowNodesV2Params,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof getWorkflowNodesV2>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
) => {
  const { query: queryOptions, request: requestOptions } = options ?? {};

  const queryKey = queryOptions?.queryKey ?? getGetWorkflowNodesV2QueryKey(params);

  const queryFn: QueryFunction<Awaited<ReturnType<typeof getWorkflowNodesV2>>> = ({ signal }) =>
    getWorkflowNodesV2(params, requestOptions, signal);

  return { queryKey, queryFn, ...queryOptions } as UseQueryOptions<
    Awaited<ReturnType<typeof getWorkflowNodesV2>>,
    TError,
    TData
  > & { queryKey: DataTag<QueryKey, TData, TError> };
};

export type GetWorkflowNodesV2QueryResult = NonNullable<Awaited<ReturnType<typeof getWorkflowNodesV2>>>;
export type GetWorkflowNodesV2QueryError = ErrorType<TLHTTPValidationError>;

export function useGetWorkflowNodesV2<
  TData = Awaited<ReturnType<typeof getWorkflowNodesV2>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  params: undefined | GetWorkflowNodesV2Params,
  options: {
    query: Partial<UseQueryOptions<Awaited<ReturnType<typeof getWorkflowNodesV2>>, TError, TData>> &
      Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof getWorkflowNodesV2>>,
          TError,
          Awaited<ReturnType<typeof getWorkflowNodesV2>>
        >,
        'initialData'
      >;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useGetWorkflowNodesV2<
  TData = Awaited<ReturnType<typeof getWorkflowNodesV2>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  params?: GetWorkflowNodesV2Params,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof getWorkflowNodesV2>>, TError, TData>> &
      Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof getWorkflowNodesV2>>,
          TError,
          Awaited<ReturnType<typeof getWorkflowNodesV2>>
        >,
        'initialData'
      >;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useGetWorkflowNodesV2<
  TData = Awaited<ReturnType<typeof getWorkflowNodesV2>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  params?: GetWorkflowNodesV2Params,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof getWorkflowNodesV2>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
/**
 * @summary Get Workflow Nodes V2
 */

export function useGetWorkflowNodesV2<
  TData = Awaited<ReturnType<typeof getWorkflowNodesV2>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  params?: GetWorkflowNodesV2Params,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof getWorkflowNodesV2>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {
  const queryOptions = getGetWorkflowNodesV2QueryOptions(params, options);

  const query = useQuery(queryOptions, queryClient) as UseQueryResult<TData, TError> & {
    queryKey: DataTag<QueryKey, TData, TError>;
  };

  return withQueryKey(query, queryOptions.queryKey);
}

/**
 * @summary Get Workflow Nodes V2
 */
export const prefetchGetWorkflowNodesV2Query = async <
  TData = Awaited<ReturnType<typeof getWorkflowNodesV2>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  queryClient: QueryClient,
  params?: GetWorkflowNodesV2Params,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof getWorkflowNodesV2>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
): Promise<QueryClient> => {
  const queryOptions = getGetWorkflowNodesV2QueryOptions(params, options);

  await queryClient.prefetchQuery(queryOptions);

  return queryClient;
};

/**
 * @summary Get Workflow Nodes V2
 */
export const invalidateGetWorkflowNodesV2 = async (
  queryClient: QueryClient,
  params?: GetWorkflowNodesV2Params,
  options?: InvalidateOptions,
): Promise<QueryClient> => {
  await queryClient.invalidateQueries({ queryKey: getGetWorkflowNodesV2QueryKey(params) }, options);

  return queryClient;
};

/**
 * @summary Get Workflow Mcp Tools V2
 */
export const getWorkflowMcpToolsV2 = (options?: SecondParameter<typeof apiInstance>, signal?: AbortSignal) => {
  return apiInstance<TLMcpToolsResponse>({ url: `/api/v2/workflows/nodes/mcp-tools`, method: 'GET', signal }, options);
};

export const getGetWorkflowMcpToolsV2QueryKey = () => {
  return ['api', 'v2', 'workflows', 'nodes', 'mcp-tools'] as const;
};

export const getGetWorkflowMcpToolsV2QueryOptions = <
  TData = Awaited<ReturnType<typeof getWorkflowMcpToolsV2>>,
  TError = ErrorType<unknown>,
>(options?: {
  query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof getWorkflowMcpToolsV2>>, TError, TData>>;
  request?: SecondParameter<typeof apiInstance>;
}) => {
  const { query: queryOptions, request: requestOptions } = options ?? {};

  const queryKey = queryOptions?.queryKey ?? getGetWorkflowMcpToolsV2QueryKey();

  const queryFn: QueryFunction<Awaited<ReturnType<typeof getWorkflowMcpToolsV2>>> = ({ signal }) =>
    getWorkflowMcpToolsV2(requestOptions, signal);

  return { queryKey, queryFn, ...queryOptions } as UseQueryOptions<
    Awaited<ReturnType<typeof getWorkflowMcpToolsV2>>,
    TError,
    TData
  > & { queryKey: DataTag<QueryKey, TData, TError> };
};

export type GetWorkflowMcpToolsV2QueryResult = NonNullable<Awaited<ReturnType<typeof getWorkflowMcpToolsV2>>>;
export type GetWorkflowMcpToolsV2QueryError = ErrorType<unknown>;

export function useGetWorkflowMcpToolsV2<
  TData = Awaited<ReturnType<typeof getWorkflowMcpToolsV2>>,
  TError = ErrorType<unknown>,
>(
  options: {
    query: Partial<UseQueryOptions<Awaited<ReturnType<typeof getWorkflowMcpToolsV2>>, TError, TData>> &
      Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof getWorkflowMcpToolsV2>>,
          TError,
          Awaited<ReturnType<typeof getWorkflowMcpToolsV2>>
        >,
        'initialData'
      >;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useGetWorkflowMcpToolsV2<
  TData = Awaited<ReturnType<typeof getWorkflowMcpToolsV2>>,
  TError = ErrorType<unknown>,
>(
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof getWorkflowMcpToolsV2>>, TError, TData>> &
      Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof getWorkflowMcpToolsV2>>,
          TError,
          Awaited<ReturnType<typeof getWorkflowMcpToolsV2>>
        >,
        'initialData'
      >;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useGetWorkflowMcpToolsV2<
  TData = Awaited<ReturnType<typeof getWorkflowMcpToolsV2>>,
  TError = ErrorType<unknown>,
>(
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof getWorkflowMcpToolsV2>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
/**
 * @summary Get Workflow Mcp Tools V2
 */

export function useGetWorkflowMcpToolsV2<
  TData = Awaited<ReturnType<typeof getWorkflowMcpToolsV2>>,
  TError = ErrorType<unknown>,
>(
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof getWorkflowMcpToolsV2>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {
  const queryOptions = getGetWorkflowMcpToolsV2QueryOptions(options);

  const query = useQuery(queryOptions, queryClient) as UseQueryResult<TData, TError> & {
    queryKey: DataTag<QueryKey, TData, TError>;
  };

  return withQueryKey(query, queryOptions.queryKey);
}

/**
 * @summary Get Workflow Mcp Tools V2
 */
export const prefetchGetWorkflowMcpToolsV2Query = async <
  TData = Awaited<ReturnType<typeof getWorkflowMcpToolsV2>>,
  TError = ErrorType<unknown>,
>(
  queryClient: QueryClient,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof getWorkflowMcpToolsV2>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
): Promise<QueryClient> => {
  const queryOptions = getGetWorkflowMcpToolsV2QueryOptions(options);

  await queryClient.prefetchQuery(queryOptions);

  return queryClient;
};

/**
 * @summary Get Workflow Mcp Tools V2
 */
export const invalidateGetWorkflowMcpToolsV2 = async (
  queryClient: QueryClient,
  options?: InvalidateOptions,
): Promise<QueryClient> => {
  await queryClient.invalidateQueries({ queryKey: getGetWorkflowMcpToolsV2QueryKey() }, options);

  return queryClient;
};

/**
 * @summary Get Workflow Node V2
 */
export const getWorkflowNodeV2 = (
  { nodeName }: GetWorkflowNodeV2PathParameters,
  options?: SecondParameter<typeof apiInstance>,
  signal?: AbortSignal,
) => {
  return apiInstance<TLNodeDefinition>({ url: `/api/v2/workflows/nodes/${nodeName}`, method: 'GET', signal }, options);
};

export const getGetWorkflowNodeV2QueryKey = ({ nodeName }: GetWorkflowNodeV2PathParameters) => {
  return ['api', 'v2', 'workflows', 'nodes', nodeName] as const;
};

export const getGetWorkflowNodeV2QueryOptions = <
  TData = Awaited<ReturnType<typeof getWorkflowNodeV2>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  { nodeName }: GetWorkflowNodeV2PathParameters,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof getWorkflowNodeV2>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
) => {
  const { query: queryOptions, request: requestOptions } = options ?? {};

  const queryKey = queryOptions?.queryKey ?? getGetWorkflowNodeV2QueryKey({ nodeName });

  const queryFn: QueryFunction<Awaited<ReturnType<typeof getWorkflowNodeV2>>> = ({ signal }) =>
    getWorkflowNodeV2({ nodeName }, requestOptions, signal);

  return {
    queryKey,
    queryFn,
    enabled: nodeName !== null && nodeName !== undefined,
    ...queryOptions,
  } as UseQueryOptions<Awaited<ReturnType<typeof getWorkflowNodeV2>>, TError, TData> & {
    queryKey: DataTag<QueryKey, TData, TError>;
  };
};

export type GetWorkflowNodeV2QueryResult = NonNullable<Awaited<ReturnType<typeof getWorkflowNodeV2>>>;
export type GetWorkflowNodeV2QueryError = ErrorType<TLHTTPValidationError>;

export function useGetWorkflowNodeV2<
  TData = Awaited<ReturnType<typeof getWorkflowNodeV2>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  pathParams: GetWorkflowNodeV2PathParameters,
  options: {
    query: Partial<UseQueryOptions<Awaited<ReturnType<typeof getWorkflowNodeV2>>, TError, TData>> &
      Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof getWorkflowNodeV2>>,
          TError,
          Awaited<ReturnType<typeof getWorkflowNodeV2>>
        >,
        'initialData'
      >;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useGetWorkflowNodeV2<
  TData = Awaited<ReturnType<typeof getWorkflowNodeV2>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  pathParams: GetWorkflowNodeV2PathParameters,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof getWorkflowNodeV2>>, TError, TData>> &
      Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof getWorkflowNodeV2>>,
          TError,
          Awaited<ReturnType<typeof getWorkflowNodeV2>>
        >,
        'initialData'
      >;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useGetWorkflowNodeV2<
  TData = Awaited<ReturnType<typeof getWorkflowNodeV2>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  pathParams: GetWorkflowNodeV2PathParameters,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof getWorkflowNodeV2>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
/**
 * @summary Get Workflow Node V2
 */

export function useGetWorkflowNodeV2<
  TData = Awaited<ReturnType<typeof getWorkflowNodeV2>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  { nodeName }: GetWorkflowNodeV2PathParameters,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof getWorkflowNodeV2>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {
  const queryOptions = getGetWorkflowNodeV2QueryOptions({ nodeName }, options);

  const query = useQuery(queryOptions, queryClient) as UseQueryResult<TData, TError> & {
    queryKey: DataTag<QueryKey, TData, TError>;
  };

  return withQueryKey(query, queryOptions.queryKey);
}

/**
 * @summary Get Workflow Node V2
 */
export const prefetchGetWorkflowNodeV2Query = async <
  TData = Awaited<ReturnType<typeof getWorkflowNodeV2>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  queryClient: QueryClient,
  { nodeName }: GetWorkflowNodeV2PathParameters,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof getWorkflowNodeV2>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
): Promise<QueryClient> => {
  const queryOptions = getGetWorkflowNodeV2QueryOptions({ nodeName }, options);

  await queryClient.prefetchQuery(queryOptions);

  return queryClient;
};

/**
 * @summary Get Workflow Node V2
 */
export const invalidateGetWorkflowNodeV2 = async (
  queryClient: QueryClient,
  { nodeName }: GetWorkflowNodeV2PathParameters,
  options?: InvalidateOptions,
): Promise<QueryClient> => {
  await queryClient.invalidateQueries({ queryKey: getGetWorkflowNodeV2QueryKey({ nodeName }) }, options);

  return queryClient;
};

/**
 * @summary Get Workflow Node Docs V2
 */
export const getWorkflowNodeDocsV2 = (
  { nodeName }: GetWorkflowNodeDocsV2PathParameters,
  options?: SecondParameter<typeof apiInstance>,
  signal?: AbortSignal,
) => {
  return apiInstance<TLNodeDocumentationResponse>(
    { url: `/api/v2/workflows/nodes/${nodeName}/docs`, method: 'GET', signal },
    options,
  );
};

export const getGetWorkflowNodeDocsV2QueryKey = ({ nodeName }: GetWorkflowNodeDocsV2PathParameters) => {
  return ['api', 'v2', 'workflows', 'nodes', nodeName, 'docs'] as const;
};

export const getGetWorkflowNodeDocsV2QueryOptions = <
  TData = Awaited<ReturnType<typeof getWorkflowNodeDocsV2>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  { nodeName }: GetWorkflowNodeDocsV2PathParameters,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof getWorkflowNodeDocsV2>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
) => {
  const { query: queryOptions, request: requestOptions } = options ?? {};

  const queryKey = queryOptions?.queryKey ?? getGetWorkflowNodeDocsV2QueryKey({ nodeName });

  const queryFn: QueryFunction<Awaited<ReturnType<typeof getWorkflowNodeDocsV2>>> = ({ signal }) =>
    getWorkflowNodeDocsV2({ nodeName }, requestOptions, signal);

  return {
    queryKey,
    queryFn,
    enabled: nodeName !== null && nodeName !== undefined,
    ...queryOptions,
  } as UseQueryOptions<Awaited<ReturnType<typeof getWorkflowNodeDocsV2>>, TError, TData> & {
    queryKey: DataTag<QueryKey, TData, TError>;
  };
};

export type GetWorkflowNodeDocsV2QueryResult = NonNullable<Awaited<ReturnType<typeof getWorkflowNodeDocsV2>>>;
export type GetWorkflowNodeDocsV2QueryError = ErrorType<TLHTTPValidationError>;

export function useGetWorkflowNodeDocsV2<
  TData = Awaited<ReturnType<typeof getWorkflowNodeDocsV2>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  pathParams: GetWorkflowNodeDocsV2PathParameters,
  options: {
    query: Partial<UseQueryOptions<Awaited<ReturnType<typeof getWorkflowNodeDocsV2>>, TError, TData>> &
      Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof getWorkflowNodeDocsV2>>,
          TError,
          Awaited<ReturnType<typeof getWorkflowNodeDocsV2>>
        >,
        'initialData'
      >;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useGetWorkflowNodeDocsV2<
  TData = Awaited<ReturnType<typeof getWorkflowNodeDocsV2>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  pathParams: GetWorkflowNodeDocsV2PathParameters,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof getWorkflowNodeDocsV2>>, TError, TData>> &
      Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof getWorkflowNodeDocsV2>>,
          TError,
          Awaited<ReturnType<typeof getWorkflowNodeDocsV2>>
        >,
        'initialData'
      >;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useGetWorkflowNodeDocsV2<
  TData = Awaited<ReturnType<typeof getWorkflowNodeDocsV2>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  pathParams: GetWorkflowNodeDocsV2PathParameters,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof getWorkflowNodeDocsV2>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
/**
 * @summary Get Workflow Node Docs V2
 */

export function useGetWorkflowNodeDocsV2<
  TData = Awaited<ReturnType<typeof getWorkflowNodeDocsV2>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  { nodeName }: GetWorkflowNodeDocsV2PathParameters,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof getWorkflowNodeDocsV2>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {
  const queryOptions = getGetWorkflowNodeDocsV2QueryOptions({ nodeName }, options);

  const query = useQuery(queryOptions, queryClient) as UseQueryResult<TData, TError> & {
    queryKey: DataTag<QueryKey, TData, TError>;
  };

  return withQueryKey(query, queryOptions.queryKey);
}

/**
 * @summary Get Workflow Node Docs V2
 */
export const prefetchGetWorkflowNodeDocsV2Query = async <
  TData = Awaited<ReturnType<typeof getWorkflowNodeDocsV2>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  queryClient: QueryClient,
  { nodeName }: GetWorkflowNodeDocsV2PathParameters,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof getWorkflowNodeDocsV2>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
): Promise<QueryClient> => {
  const queryOptions = getGetWorkflowNodeDocsV2QueryOptions({ nodeName }, options);

  await queryClient.prefetchQuery(queryOptions);

  return queryClient;
};

/**
 * @summary Get Workflow Node Docs V2
 */
export const invalidateGetWorkflowNodeDocsV2 = async (
  queryClient: QueryClient,
  { nodeName }: GetWorkflowNodeDocsV2PathParameters,
  options?: InvalidateOptions,
): Promise<QueryClient> => {
  await queryClient.invalidateQueries({ queryKey: getGetWorkflowNodeDocsV2QueryKey({ nodeName }) }, options);

  return queryClient;
};
